import React, { useState, useMemo, useEffect } from 'react';
import DatePicker from './DatePicker';

interface DateRangePickerProps {
  minDate: string; // ISO date string (YYYY-MM-DD)
  maxDate: string; // ISO date string (YYYY-MM-DD)
  startDate?: string; // ISO date string (YYYY-MM-DD)
  endDate?: string; // ISO date string (YYYY-MM-DD)
  onChange: (startDate: string, endDate: string) => void;
  className?: string;
  disabled?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  minDate,
  maxDate,
  startDate,
  endDate,
  onChange,
  className = '',
  disabled = false
}) => {
  const [startDateValue, setStartDateValue] = useState<string>(startDate || '');
  const [endDateValue, setEndDateValue] = useState<string>(endDate || '');
  const [startDateError, setStartDateError] = useState<string>('');
  const [endDateError, setEndDateError] = useState<string>('');

  // Update local state when props change
  useEffect(() => {
    if (startDate !== undefined) {
      setStartDateValue(startDate);
    } else {
      setStartDateValue('');
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate !== undefined) {
      setEndDateValue(endDate);
    } else {
      setEndDateValue('');
    }
  }, [endDate]);

  // Calculate max date for start picker (shouldn't be after end date)
  const startMaxDate = useMemo(() => {
    if (endDateValue) {
      return endDateValue;
    }
    return maxDate;
  }, [endDateValue, maxDate]);

  // Calculate min date for end picker (shouldn't be before start date)
  const endMinDate = useMemo(() => {
    if (startDateValue) {
      return startDateValue;
    }
    return minDate;
  }, [startDateValue, minDate]);

  // Handle start date change
  const handleStartDateChange = (newStartDate: string) => {
    setStartDateError('');
    
    // Validate that start date is not after end date
    if (endDateValue && newStartDate && newStartDate > endDateValue) {
      setStartDateError('Start date cannot be after end date');
      return;
    }

    setStartDateValue(newStartDate);
    onChange(newStartDate || '', endDateValue || '');
  };

  // Handle end date change
  const handleEndDateChange = (newEndDate: string) => {
    setEndDateError('');
    
    // Validate that end date is not before start date
    if (startDateValue && newEndDate && newEndDate < startDateValue) {
      setEndDateError('End date cannot be before start date');
      return;
    }

    setEndDateValue(newEndDate);
    onChange(startDateValue || '', newEndDate || '');
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Date Pickers Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
        {/* Start Date Picker */}
        <div>
          <DatePicker
            value={startDateValue}
            onChange={handleStartDateChange}
            minDate={minDate}
            maxDate={startMaxDate}
            placeholder="Start date"
            disabled={disabled}
            error={startDateError}
            className="w-full"
          />
        </div>

        {/* End Date Picker */}
        <div>
          <DatePicker
            value={endDateValue}
            onChange={handleEndDateChange}
            minDate={endMinDate}
            maxDate={maxDate}
            placeholder="End date"
            disabled={disabled}
            error={endDateError}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;

